import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { checkIsActive } from "../../../../../_helpers";
import { Radio, Checkbox, Empty, Popover } from "antd";
import { getPercentage } from "../../../../../../app/pages/helper";
import { useResultStore } from "../../../useResultStore";
import { DoubleLeftOutlined, FileDoneOutlined} from "@ant-design/icons";
import useAsideStore from "../../AsideStore";
import { useTranslation } from "../../../../../../app/i18n/LanguageContext";

function MenuResultsList({ menuItem, kind }) {
   const { t } = useTranslation();
   //  const [activeAssessment, setActiveAssessment] = useState(menuItem?.length ? menuItem[0].id : 0);
   // const [activeResult, setActiveResult] = useState("");

   const { activeResult, setActiveResult, isExpertInsightsActive } = useResultStore();
   const { collapsedSecodary, setCollapsedSecondary } = useAsideStore();

   // Expert Insights only applies to guided assessments, so while its tab is open
   // the organization's list hides self assessments. `isGuided === false` marks a
   // self assessment (mirroring the G/S badge below); everything else is guided.
   const visibleItems = useMemo(() => {
      const items = Array.isArray(menuItem) ? menuItem : [];
      if (!isExpertInsightsActive) return items;
      return items.filter((item) => item?.isGuided !== false);
   }, [menuItem, isExpertInsightsActive]);

   const location = useLocation();
   const navigate = useNavigate();
   const params = useParams();

   const getMenuItemActive = (url, hasSubmenu = false) => {
      return checkIsActive(location, url)
         ? ` ${!hasSubmenu && "menu-item-active"} menu-item-open `
         : "";
   };

   const getColor = (completed, number) => {
      if (number) {
         const percent = completed * (100 / number);
         const hue = ((percent / 100) * 120).toString(10);
         return ["hsl(", hue, ",100%,42%)"].join("");
      } else {
         return "#DDE9FF";
      }
   };

   const persistSelectedAssessmentMeta = useCallback((assessmentId) => {
      const selectedAssessment = menuItem?.find(
         (item) => String(item?.id) === String(assessmentId)
      );
      if (!selectedAssessment) return;

      localStorage.setItem("selectedAssessmentId", String(assessmentId));
      if (selectedAssessment?.name) {
         localStorage.setItem("selectedAssessmentName", selectedAssessment.name);
      }
      localStorage.setItem(
         "selectedAssessmentIsLinked",
         String(Boolean(selectedAssessment?.isLinked))
      );

      const percentage = Number(
         getPercentage(selectedAssessment?.score, selectedAssessment?.maxScore)
      );
      const safePercentage = Number.isFinite(percentage) ? Math.round(percentage) : 0;
      localStorage.setItem("selectedAssessmentScore", `${safePercentage}%`);
      localStorage.setItem(
         "selectedAssessmentScoreColor",
         getColor(selectedAssessment?.score, selectedAssessment?.maxScore)
      );
   }, [menuItem]);

   const clickedCard = useCallback((e) => {
      // setActiveResult((prev) => prev.includes(e) ? prev.filter(it => it !== e) : prev.concat(e));
      // setActiveResult(activeResult.includes(e) ? activeResult.filter(it => it !== e) : activeResult.concat(e));
      setActiveResult([e]);
      persistSelectedAssessmentMeta(e);

      navigate(`/workspace/results/results/${e}`);
   }, [navigate, persistSelectedAssessmentMeta, setActiveResult]);

   useEffect(() => {
      if (menuItem?.length === 0) {
         navigate(`/workspace/results/results/no-result`);
      }
   }, [menuItem, navigate]);

   useEffect(() => {
      if (params["*"].includes("workspace/results/results")) {
         if (params["*"].split("/").length > 3 && params["*"].split("/")[3]) {
            const selectedAssessmentIdFromUrl = params["*"].split("/")[3];
            setActiveResult([selectedAssessmentIdFromUrl]);
            persistSelectedAssessmentMeta(selectedAssessmentIdFromUrl);
         } else {
            if (menuItem?.length) {
               clickedCard(menuItem[0].id);
            }
         }
      }
   }, [clickedCard, menuItem, params, persistSelectedAssessmentMeta, setActiveResult]);

   return (
      <>
         <div className="assessments-menu-list">
            <DoubleLeftOutlined className={`collapse-button ${collapsedSecodary && 'reversed'}`} onClick={() => setCollapsedSecondary(!collapsedSecodary)}/>
            <div className="assessment-content">
               <div className="assessments-menu-options">
                  <div className="assessments-menu-title-items">
                     <h2>{t("menu-item-assessments")}</h2>
                  </div>
                  <div className="assessments-menu-sort">
                     <h3 className="assessment-sort-title">
                        {t("select-assessment-see-results-text")}
                     </h3>
                  </div>
               </div>
               <div className="assessments-cards">
                  {visibleItems?.length ? (
                     <Checkbox.Group value={activeResult}>
                        {visibleItems?.map((item, i) => {
                           return (
                              <div
                                 className={`assessments-card ${
                                    activeResult?.includes(item?.id) ? "activeCard" : ""
                                 }`}
                                 key={item?.id}
                                 onClick={() => clickedCard(item?.id)}
                              >
                                 <div className="assessments-card-inner" style={{ gap: "10px" }}>
                                    <div className="assessments-card-upper">
                                       <h3 className="assessment-title">
                                          {item?.name}
                                          {item?.isLinked ? (
                                             <span className="assessment-linked-badge">{t("linked-badge-label")}</span>
                                          ) : null}
                                       </h3>
                                       <Checkbox value={item?.id}></Checkbox>
                                    </div>
                                    <div className="assessment-score">
                                       <span
                                          className="assessment-results-result"
                                          style={{
                                             color: getColor(item?.score, item?.maxScore)
                                          }}
                                       >{`${getPercentage(item?.score, item?.maxScore).toFixed(
                                          0
                                       )}%`}</span>
                                    </div>
                                    {item?.respondedOrganizationsCount !== undefined &&
                                       item?.totalOrganizationsCount !== undefined && (
                                          <div className="assessment-respondents">
                                             {item.respondedOrganizationsCount} {t("of-word")}{" "}
                                             {item.totalOrganizationsCount} {t("respondents-count-label")}
                                          </div>
                                       )}
                                    <div
                                       className="assessment-results justify-content-between"
                                       style={{ gap: "10px" }}
                                    >
                                       <span className="assessment-results-text">
                                             {t("latest-round-label")}: {item?.lastLabel}
                                       </span>
                                       <span className={`assessment-results-type ${item.isGuided === false ? 'self' : 'guided'}`}>
                                          {item.isGuided === false ?
                                             <span> S </span> :
                                             <span> G </span>
                                          }
                                       </span>
                                    </div>
                                 </div>
                              </div>
                           );
                        })}
                     </Checkbox.Group>
                  ) : (
                     <div className="no-active-assessment">
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="" />
                        <h2>{t("no-assessments-available-title")}</h2>
                        <p>{t("start-adding-view-results-text")}</p>
                     </div>
                  )}
               </div>
            </div>
            <div className={`selected-assessment-indicator ${collapsedSecodary && 'opened'}`}>
               <FileDoneOutlined/>
               {
                  activeResult?.length &&
                  <Popover 
                     placement="right"
                     classNames={{
                        root: 'selected-assessment-popover',
                        content: 'selected-assessment-popover',
                     }}
                     color="rgba(0, 0, 0, 0.75)"
                     title={`${activeResult?.length} ${t("assessment-selected-suffix")}`}
                     content={<ul>{visibleItems?.map((e) => activeResult?.includes(e.id) && <li>{e.name}</li>)}</ul>}>

                     <div className="number-indicator">{activeResult?.length}</div>
                  </Popover>
               }
            </div>
         </div>
      </>
   );
}

export default MenuResultsList;
